import { LinkButton, Loader, Text } from "components";
import { FC, useCallback } from "react";

import { useExtendSubscriptionMutation, useGetCsrfTokenQuery, useGetMySubscriptionsQuery } from "src/api";
import { showSuccessNotification } from "src/notifications";
import { Trans, useTranslation } from "src/translations";
import { useGetExpirationDate } from "../utils";

export const ExtendSubscription: FC = () => {
    const { t } = useTranslation();

    const { daysToExpiration, expirationDate } = useGetExpirationDate();
    const { data: subscription, isFetching: isSubscriptionFetching } = useGetMySubscriptionsQuery();
    const { data: csrfTokenResponse } = useGetCsrfTokenQuery();

    const [extend] = useExtendSubscriptionMutation();
    const extendSubscription = useCallback(async () => {
        try {
            await extend(csrfTokenResponse!.token!).unwrap();
            showSuccessNotification({
                title: t("subscriptions.extend.successTitle"),
                message: t("subscriptions.extend.successMessage"),
            });
        } catch (e) {
            // handled by global error handling
        }
    }, [csrfTokenResponse, extend, t]);

    if (isSubscriptionFetching) {
        return <Loader />;
    }

    if (!subscription || typeof daysToExpiration !== "number") {
        return null;
    }

    // Subscription is active through its expiration day (backend sends notifications while expiration >= today)
    const isExpired = daysToExpiration < 0;
    const expiresToday = daysToExpiration === 0;

    let i18nKey = "subscriptions.extend.description";
    if (isExpired) {
        i18nKey = "subscriptions.extend.expiredDescription";
    } else if (expiresToday) {
        i18nKey = "subscriptions.extend.todayDescription";
    }

    return (
        <div>
            <Trans
                i18nKey={i18nKey}
                values={{
                    daysToExpiration,
                    days: t("subscriptions.extend.days", { count: daysToExpiration }),
                    expirationDate,
                }}
            >
                <Text />
                <LinkButton onClick={extendSubscription} />
                <Text />
            </Trans>
        </div>
    );
};
